import { useMutation } from '@tanstack/react-query';
import {
  CLOSURE_REASON_LABELS,
  CLOSURE_REASONS,
  type ClosureReason,
  closureError,
  type FileDownloadResponse,
  formatMoney,
  type InvoiceListItem,
  type InvoiceResponse,
  italianToday,
  parseDecimal,
  UPLOAD_EXTENSIONS,
  uploadError,
} from '@k-management/shared';
import { useState } from 'react';
import { Link } from 'react-router';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { DecimalInput } from '../../components/DecimalInput.js';
import { Field } from '../../components/Field.js';
import { apiRequest, apiUpload } from '../../lib/api.js';

/**
 * «Chiudi senza movimento» (2.08): the residual of an invoice, or a part of
 * it, closed without money for a reason, with notes and, if any, the document,
 * filed in the folder of the subject › Tesoreria. For the CFO
 * (tesoreria.closures); only for invoices with their subject in Anagrafica.
 */
export function ClosureDialog({
  invoice,
  onClose,
  onSaved,
}: {
  invoice: Pick<InvoiceListItem, 'id' | 'number' | 'kind' | 'residual' | 'subjectId'>;
  onClose: () => void;
  onSaved: (updated: InvoiceResponse) => Promise<void> | void;
}) {
  const [date, setDate] = useState(italianToday());
  const [amount, setAmount] = useState(invoice.residual);
  const [reason, setReason] = useState<ClosureReason | ''>('');
  const [note, setNote] = useState('');
  const [file, setFile] = useState<File | null>(null);
  // Closed, with the document still to send (SharePoint did not take it): only the file again.
  const [closed, setClosed] = useState<InvoiceResponse | null>(null);
  const number = parseDecimal(amount);
  const amountError = number === null ? null : closureError(invoice.residual, number.toFixed(2));
  const fileError = file ? uploadError(file.name, file.size) : null;
  const ready =
    number !== null && !amountError && reason !== '' && note.trim().length >= 3 && !fileError;

  const save = useMutation({
    mutationFn: async () => {
      let updated = closed;
      if (!updated) {
        updated = await apiRequest<InvoiceResponse>(`/treasury/invoices/${invoice.id}/closures`, {
          method: 'POST',
          body: { date, amount: number!.toFixed(2), reason, note: note.trim() },
        });
        setClosed(updated);
      }
      if (!file) return updated;
      // The newest closure of the invoice is the one just made.
      const settlement = [...updated.settlements]
        .filter((item) => item.closure)
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0]!;
      return apiUpload<InvoiceResponse>(
        `/treasury/invoices/${invoice.id}/closures/${settlement.id}/document?name=${encodeURIComponent(file.name)}`,
        file,
      );
    },
    onSuccess: onSaved,
  });

  if (!invoice.subjectId) {
    return (
      <ConfirmDialog
        title={`Chiudi senza movimento · ${invoice.number}`}
        confirmLabel="Chiudi"
        onConfirm={onClose}
        onCancel={onClose}
      >
        <p>
          La fattura non è abbinata a un soggetto dell’Anagrafica: ogni fattura e ogni movimento
          devono avere il loro soggetto. Abbinala prima, poi chiudila.
        </p>
        <p>
          <Link to="/tesoreria/anagrafica-dalle-fatture">Tesoreria › Anagrafica dalle fatture</Link>
        </p>
      </ConfirmDialog>
    );
  }

  return (
    <ConfirmDialog
      title={`Chiudi senza movimento · ${invoice.number}`}
      confirmLabel={closed ? 'Carica il documento' : 'Chiudi la fattura'}
      busy={save.isPending}
      disabled={!ready}
      error={
        closed && save.error
          ? `La fattura è chiusa, ma il documento non è stato caricato: ${save.error.message}`
          : save.error?.message
      }
      onConfirm={() => save.mutate()}
      onCancel={() => (closed ? void onSaved(closed) : onClose())}
    >
      <p className="muted small">
        Il residuo si chiude senza denaro: non va in banca, non conta come incasso o pagamento nel
        budget di Tesoreria della commessa, resta nella storia della fattura e nel Registro
        attività.
      </p>
      <div className="form-grid">
        <Field label="Data">
          <input
            type="date"
            value={date}
            disabled={Boolean(closed)}
            onChange={(event) => setDate(event.target.value)}
          />
        </Field>
        <Field
          label="Importo"
          error={amountError ?? undefined}
          hint={
            number !== null && !amountError && Math.abs(number) < Math.abs(Number(invoice.residual))
              ? `Meno del residuo (${formatMoney(invoice.residual)}): resta aperto il resto.`
              : invoice.kind === 'nota_credito'
                ? 'Nota di credito: l’importo è negativo.'
                : 'Proposto il residuo.'
          }
        >
          <DecimalInput
            value={amount}
            onChange={setAmount}
            decimals={[2, 2]}
            disabled={Boolean(closed)}
          />
        </Field>
      </div>
      <Field label="Motivazione">
        <select
          value={reason}
          disabled={Boolean(closed)}
          onChange={(event) => setReason(event.target.value as ClosureReason | '')}
        >
          <option value="">Scegli…</option>
          {CLOSURE_REASONS.map((item) => (
            <option key={item} value={item}>
              {CLOSURE_REASON_LABELS[item]}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Note" hint="Per esempio: accordo del 18/09/2025, saldo e stralcio.">
        <textarea
          rows={3}
          maxLength={300}
          value={note}
          disabled={Boolean(closed)}
          onChange={(event) => setNote(event.target.value)}
        />
      </Field>
      <Field
        label="Documento (facoltativo)"
        error={fileError ?? undefined}
        hint="Va nella cartella SharePoint del soggetto, sotto «Tesoreria»."
      >
        <input
          type="file"
          accept={UPLOAD_EXTENSIONS.map((extension) => `.${extension}`).join(',')}
          onChange={(event) => setFile(event.target.files?.[0] ?? null)}
        />
      </Field>
    </ConfirmDialog>
  );
}

/** Opens the document of a closure in a new tab, through a short-lived link of the API. */
export function useClosureDocument(invoiceId: string) {
  return useMutation({
    mutationFn: (settlementId: string) =>
      apiRequest<FileDownloadResponse>(
        `/treasury/invoices/${invoiceId}/closures/${settlementId}/document`,
      ),
    onSuccess: (link) => window.open(link.url, '_blank', 'noopener'),
  });
}
